import React from 'react';
import { motion } from 'framer-motion';

function InstitutionalServices() {
  const services = [
    {
      title: 'Custom Strategies',
      description: 'Tailored investment strategies designed specifically for institutional clients with unique requirements and constraints.',
      features: ['Bespoke Solutions', 'Risk Customization', 'Liquidity Management', 'ESG Integration']
    },
    {
      title: 'Execution Services',
      description: 'Professional execution services with access to global markets and advanced trading capabilities.',
      features: ['Global Market Access', 'Algorithmic Trading', 'Block Trading', 'Cost Optimization']
    },
    {
      title: 'Performance Reporting',
      description: 'Comprehensive reporting suite providing detailed insights into portfolio performance and attribution.',
      features: ['Real-time Reporting', 'Performance Attribution', 'Risk Analytics', 'Custom Dashboards']
    },
    {
      title: 'Compliance Support',
      description: 'Full compliance support to ensure adherence to regulatory requirements and institutional policies.',
      features: ['Regulatory Compliance', 'Policy Implementation', 'Audit Support', 'Documentation']
    }
  ];

  const clientTypes = [
    {
      title: 'Pension Funds',
      description: 'Long-term investment solutions for pension fund managers seeking sustainable returns.',
      image: '/images/real-people/business-people-tablet-plan-consulting-office-collaboration-unity-proposal-coworkers-workplace-teamwork-reading-report-support-online-review-networking_590464-305736.jpg'
    },
    {
      title: 'Endowments',
      description: 'Strategic asset allocation and investment management for educational and charitable institutions.',
      image: '/images/real-people/portrait-businessman-smile-meeting-strategy-teamwork-planning-project-office-diversity-collaboration-brainstorming-company-with-pride-documents-conference-room_590464-372491.jpg'
    },
    {
      title: 'Insurance Companies',
      description: 'Risk-adjusted investment solutions tailored to insurance company capital requirements.',
      image: '/images/real-people/inspiring-innovation-with-their-every-move-cropped-shot-group-businesspeople-working-together-office_590464-10869.jpg'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-[60vh] overflow-hidden">
        <img 
          src="/images/real-people/business-meeting-office_1268-21537.png"
          alt="Institutional Services"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/60 to-black/80" />
        
        <div className="relative z-10 flex h-full items-center justify-center text-center px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            <h1 className="text-white text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-light tracking-[-0.02em] mb-6">
              <span className="block font-light">Institutional</span>
              <span className="block font-medium text-[#fbbf24]">Services</span>
            </h1>
            <p className="text-white/90 text-xl sm:text-2xl font-light leading-relaxed max-w-3xl mx-auto">
              Bespoke investment solutions for institutional clients
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div
            data-aos="fade-up"
            data-aos-delay="100"
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-semibold text-gray-900 mb-4">Comprehensive Institutional Solutions</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto text-center">
              Professional investment services designed for institutional clients with complex requirements
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <div
                key={service.title}
                data-aos="fade-up"
                data-aos-delay={`${200 + index * 150}`}
                className="bg-gradient-to-br from-gray-50 to-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-500 p-8 border border-gray-100"
              >
                <div className="w-12 h-12 rounded-full bg-[#0056d7] flex items-center justify-center shadow-md mb-6">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-white">
                    <path d="M3 21h18" />
                    <path d="M6 21V9l6-4 6 4v12" />
                    <path d="M9 21v-6h6v6" />
                  </svg>
                </div>
                <h3 className="text-2xl font-semibold text-gray-900 mb-4">{service.title}</h3>
                <p className="text-gray-600 leading-relaxed mb-6">{service.description}</p>
                
                <ul className="space-y-3">
                  {service.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start gap-3">
                      <div className="w-2 h-2 bg-[#0056d7] rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Client Types Section */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="max-w-7xl mx-auto px-6">
          <div
            data-aos="fade-up"
            data-aos-delay="100"
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-semibold text-gray-900 mb-4">Serving Leading Institutions</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto text-center">
              Trusted by institutional clients across various sectors and geographies
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {clientTypes.map((client, index) => (
              <div
                key={client.title}
                data-aos="fade-up"
                data-aos-delay={`${200 + index * 150}`}
                className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-500 overflow-hidden"
              >
                <img 
                  src={client.image}
                  alt={client.title}
                  className="w-full h-48 object-cover"
                />
                <div className="p-8">
                  <h3 className="text-2xl font-semibold text-gray-900 mb-4">{client.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{client.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#0056d7] via-[#003a9e] to-[#002a73] text-white">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl font-light text-white mb-6">Partner with Blockfirst Capital</h2>
            <p className="text-xl text-white/90 mb-8 max-w-3xl mx-auto">
              Discover how our institutional services can support your organization's investment objectives
            </p>
            <motion.a
              href="/contact"
              className="inline-block px-12 py-6 rounded-full bg-gradient-to-r from-[#fbbf24] to-[#f59e0b] hover:from-[#f59e0b] hover:to-[#d97706] text-black font-medium text-xl transition-all duration-500 shadow-2xl hover:shadow-amber-500/30"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Schedule Consultation
            </motion.a>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default InstitutionalServices;
